/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/require-computed-property-dependencies, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { get, computed } from '@ember/object';
import { union, setDiff, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { service } from '@ember/service';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';
import {
  filterBy,
  findBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

@classNames('layout-column', 'layout-gt-sm-row')
export default class BulkSelection extends Component {
  @service
  session;

  @tracked groups = null;
  includeGroups = [];
  excludeGroups = [];

  get filteredGroups() {
    return this.groups.filter((group) => {
      if (group.type === GroupType.GROUP) {
        // Omit My Groups which the current user isn't a current staff member in.
        let staff = group.get('currentStaff') ?? [];
        return !!findBy(staff, 'personId', this.session.personId);
      }
      // Include all other groups.
      return true;
    });
  }

  @union('includeGroups', 'excludeGroups')
  selectedGroups;

  @setDiff('filteredGroups', 'selectedGroups')
  remainingGroups;

  @computed('remainingGroups')
  get sortedRemainingGroups() {
    let groupTypes = uniq(mapBy(this.remainingGroups, 'type')).sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = filterBy(
        this.remainingGroups,
        'type',
        groupType,
      );
      let groupings = uniq(mapBy(semiFilteredGroups, 'grouping')).sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...filterBy(semiFilteredGroups, 'grouping', grouping).sort(
            naturalSort('label'),
          ),
        );
      });
    });
    return sortedRemainingGroups;
  }

  @alias('session.school')
  school;

  selectedYearLevels = [];

  @setDiff('school.yearLevels', 'selectedYearLevels')
  remainingYearLevels;

  matchYearLevel(option, term) {
    let text = term.replace(/[^\d]+/g, '');
    return option.indexOf(text);
  }

  familyPositions = Object.freeze([
    { key: 'onlyChild', label: 'Only child in family' },
    { key: 'eldestOf2', label: 'Eldest of 2' },
    { key: 'eldestOf3+', label: 'Eldest of 3+' },
    { key: 'youngestOf2', label: 'Youngest of 2' },
    { key: 'youngestOf3+', label: 'Youngest of 3+' },
    { key: 'eldest', label: 'Eldest Child' },
    { key: '2ndEldest', label: '2nd Eldest' },
    { key: '3rdEldest', label: '3rd Eldest' },
    { key: '4thEldest', label: '4th Eldest' },
  ]);

  selectedPositions = [];

  @computed(
    'includeGroups.[]',
    'excludeGroups.[]',
    'selectedYearLevels.[]',
    'selectedPositions.[]',
  )
  get bulkSelectedLearners() {
    let learnerIds = [];
    if (isEmpty(this.includeGroups)) {
      // If no groups were chosen, don't bother applying the rest of the criteria as we have no learners at all.
      return learnerIds;
    }

    // First, find all learners in "include" groups.
    this.includeGroups.forEach((group) => {
      learnerIds.push(...mapBy(group.currentLearners, 'personId'));
    });

    // Remove any duplicates (learners in multiple groups)
    learnerIds = uniq(learnerIds);

    // Next, remove any learners in "exclude" groups.
    this.excludeGroups.forEach((group) => {
      let excludeIds = mapBy(group.currentLearners, 'personId');
      learnerIds = learnerIds.filter((id) => !excludeIds.includes(id));
    });

    let hasYearLevelFilter = !isEmpty(this.selectedYearLevels);
    let hasPositionFilter = !isEmpty(this.selectedPositions);
    // Lastly, iterate over the results (if any) and check against selected filters (if any).
    if (hasYearLevelFilter || hasPositionFilter) {
      // Build a map of ID to learner (for those selected above), to reduce iteration.
      let learnerMap = this.learners.reduce((map, learner) => {
        if (learnerIds.includes(learner.id)) {
          map[learner.id] = learner;
        }
        return map;
      }, {});

      // Now that we have the map, filter out any which don't match the selected filters.
      learnerIds = learnerIds.filter((learnerId) => {
        let learner = get(learnerMap, learnerId);
        if (isEmpty(learner)) {
          // Not in map, somehow.  Reject them, as we can't confirm whether they match filters.
          return false;
        }

        let include = true;

        // Apply year level filter.
        if (hasYearLevelFilter) {
          include = this.selectedYearLevels.some((yearLevel) => {
            return learner.currentYearLevel === parseInt(yearLevel, 10);
          });
        }

        // If they matched the year level filter, apply the family position filter (if any).
        if (include && hasPositionFilter) {
          include = this.selectedPositions.some((position) => {
            return this.matchesFamilyPosition(position, learner);
          });
        }

        return include;
      });
    }

    return learnerIds;
  }

  @observes('bulkSelectedLearners.[]')
  notifyChange() {
    if (this.onChange) {
      this.onChange(this.bulkSelectedLearners);
    }
  }

  @alias('bulkSelectedLearners.length')
  numBulkSelected;

  matchesFamilyPosition(position, person) {
    switch (position) {
      case 'onlyChild':
        return (
          person.get('learner.siblingPosition') === 1 &&
          person.get('learner.siblingCount') === 1
        );
      case 'eldestOf2':
        return (
          person.get('learner.siblingPosition') === 1 &&
          person.get('learner.siblingCount') === 2
        );
      case 'eldestOf3+':
        return (
          person.get('learner.siblingPosition') === 1 &&
          person.get('learner.siblingCount') >= 3
        );
      case 'youngestOf2':
        return (
          person.get('learner.siblingPosition') === 2 &&
          person.get('learner.siblingCount') === 2
        );
      case 'youngestOf3+':
        return (
          person.get('learner.siblingPosition') ===
            person.get('learner.siblingCount') &&
          person.get('learner.siblingCount') >= 3
        );
      case 'eldest':
        return person.get('learner.siblingPosition') === 1;
      case '2ndEldest':
        return person.get('learner.siblingPosition') === 2;
      case '3rdEldest':
        return person.get('learner.siblingPosition') === 3;
      case '4thEldest':
        return person.get('learner.siblingPosition') === 4;
    }

    // Invalid position specified.
    return false;
  }
}
